import axios from 'axios';
import { Cookies } from 'react-cookie';
import env from 'react-dotenv';

const cookies = new Cookies();

const REFRESH_URL = () => `${env?.REACT_APP_API_BASE_URL ?? ''}/auth/refresh`;

let inflight: Promise<string | null> | null = null;
let proactiveTimer: number | null = null;

const decodeExpMs = (token: string): number | null => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
};

const storeToken = (token: string): void => {
  cookies.set('token', token, { path: '/' });
  scheduleProactiveRefresh(token);
};

/** Swap the session's access token (impersonation enter/exit, or any flow
 *  that re-mints server-side) and arm the proactive refresh for it. */
export const adoptSessionToken = storeToken;

const callRefresh = async (): Promise<string | null> => {
  const priorToken: unknown = cookies.get('token');

  const adoptedTokenSinceRequest = (): string | null => {
    const currentToken: unknown = cookies.get('token');
    return currentToken !== priorToken &&
      typeof currentToken === 'string' &&
      currentToken
      ? currentToken
      : null;
  };

  try {
    // Bare axios: the shared instance's interceptors must never recurse into
    // refresh. The credential is the httpOnly cookie. The current (possibly
    // expired) access token rides along so the backend can preserve an
    // active impersonation scope across the rotation.
    const response = await axios.post(REFRESH_URL(), null, {
      withCredentials: true,
      timeout: 15000,
      headers:
        typeof priorToken === 'string' && priorToken
          ? { Authorization: `Bearer ${priorToken}` }
          : undefined,
    });

    // A session swap (for example, entering/exiting impersonation) wins over
    // a refresh that began under the previous token. Return the adopted token
    // as a successful no-op so callers cannot expire the replacement session.
    const adoptedToken = adoptedTokenSinceRequest();
    if (adoptedToken) return adoptedToken;

    // The token may also have been removed or replaced with an invalid value.
    // In that case this response is still stale and must never restore it.
    if (cookies.get('token') !== priorToken) return null;

    const token: unknown = response.data?.data?.token;
    if (typeof token === 'string' && token) {
      storeToken(token);
      return token;
    }
    return null;
  } catch {
    // A failed stale request must not report that a newer adopted session has
    // expired. The current token is the successful result for this caller.
    return adoptedTokenSinceRequest();
  }
};

/**
 * Rotate the httpOnly refresh cookie into a fresh access token.
 * Single-flight within the tab; cross-tab serialized via the Web Locks API so
 * racing tabs cannot double-spend the rotating refresh token.
 * Resolves with the new access token, or null when the session is over.
 */
export const refreshSession = (): Promise<string | null> => {
  if (!inflight) {
    const run = (): Promise<string | null> => callRefresh();
    const locks = (navigator as Navigator & { locks?: LockManager }).locks;
    inflight = (
      locks
        ? (locks.request('grit-session-refresh', run) as Promise<string | null>)
        : run()
    ).finally(() => {
      inflight = null;
    });
  }
  return inflight;
};

/** Refresh shortly before the access token expires so SSE streams and
 *  background polls never even see a 401. No-op for tokens without exp. */
export const scheduleProactiveRefresh = (token: string): void => {
  const expMs = decodeExpMs(token);
  if (expMs === null || typeof window === 'undefined') return;
  const lead = Math.max((expMs - Date.now()) * 0.8, 30_000);
  if (proactiveTimer !== null) window.clearTimeout(proactiveTimer);
  proactiveTimer = window.setTimeout(() => {
    proactiveTimer = null;
    void refreshSession();
  }, lead);
};

/** Arm the proactive timer for an already-logged-in tab (call at module init). */
export const initSessionRefresh = (): void => {
  const token = cookies.get('token');
  if (typeof token === 'string' && token) {
    scheduleProactiveRefresh(token);
  }
};
